/**
 * scrapLogic.ts
 * Utilities to determine if a material should be marked as scrap
 * based on business formulas.
 */

import {
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	HEX_BAR,
	PLATE,
	ANGLE,
	CHANNEL,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../../../../../config/constants';

interface ScrapCalculationParams {
	height?: number;
	width?: number;
	outerDiameter?: number;
	innerDiameter?: number;
	wallThickness?: number;
	remainingLength: number;
	shapeType?: string;
}

/**
 * Determines if a material should be considered scrap based on the following formulas:
 * - General Rule: Size1 + Size2 + Length < 4"
 *   - Size1 = Height (rectangular) or OD (circular)
 *   - Size2 = Width (rectangular) or ID (circular)
 * - Round Bar Exception: (OD × 2) + Length < 4" (no ID available)
 */
export const shouldBeScrap = ({
	height,
	width,
	outerDiameter,
	innerDiameter,
	wallThickness,
	remainingLength,
	shapeType,
}: ScrapCalculationParams): boolean => {
	// Validate that remainingLength is a valid number
	if (
		remainingLength === null ||
		remainingLength === undefined ||
		isNaN(remainingLength)
	) {
		return false;
	}

	const shapeName = shapeType || '';

	// Round Bar (no ID): (OD × 2) + Length < 4"
	if (shapeName === ROUND_BAR && outerDiameter) {
		const result = outerDiameter * 2 + remainingLength;
		return result < 4;
	}

	// Round Tube (has OD and ID): OD + ID + Length < 4"
	if (shapeName === ROUND_TUBE && outerDiameter && innerDiameter) {
		const result = outerDiameter + innerDiameter + remainingLength;
		return result < 4;
	}

	// Rectangle Tube, Angle, Channel: Height + Width + Length < 4"
	if (
		(shapeName === RECTANGLE_TUBE ||
			shapeName === ANGLE ||
			shapeName === CHANNEL) &&
		height &&
		width
	) {
		const result = height + width + remainingLength;
		return result < 4;
	}

	// Rectangle Bar, Plate: Height + Width + Length < 4"
	if ((shapeName === RECTANGLE_BAR || shapeName === PLATE) && height && width) {
		const result = height + width + remainingLength;
		return result < 4;
	}

	// Hex Bar: Height + Length < 4"
	if (shapeName === HEX_BAR && height) {
		const result = height + remainingLength;
		return result < 4;
	}

	return false;
};

/**
 * Helper to extract dimensions from a MaterialReservation object.
 * Supports both list response (material_shape_type, material_height, ...) and
 * nested material_length.material (e.g. from retrieve or other endpoints).
 */
export const extractDimensionsFromReservation = (reservation: any) => {
	const toNum = (v: any): number | undefined => {
		if (v == null || v === '') return undefined;
		const n = Number(v);
		return Number.isNaN(n) ? undefined : n;
	};
	const mat = reservation?.material_length?.material;
	const shapeFromMat =
		mat?.shape?.display_name ?? mat?.shape ?? reservation?.material_shape_type ?? '';
	return {
		height: toNum(
			reservation.material_height ??
			mat?.height
		),
		width: toNum(
			reservation.material_width ??
			mat?.width
		),
		outerDiameter: toNum(
			reservation.material_outer_diameter ??
			mat?.outer_diameter
		),
		innerDiameter: toNum(
			reservation.material_inner_diameter ??
			mat?.inner_diameter
		),
		wallThickness: toNum(
			reservation.material_wall_thickness ??
			mat?.wall_thickness
		),
		shapeType: typeof shapeFromMat === 'string' ? shapeFromMat : (shapeFromMat?.display_name ?? ''),
	};
};

/**
 * Calculates whether a material should be scrap given a full MaterialReservation
 */
export const calculateScrapStatus = (
	reservation: any,
	remainingLength: number
): boolean => {
	const dimensions = extractDimensionsFromReservation(reservation);
	return shouldBeScrap({
		...dimensions,
		remainingLength,
	});
};

/**
 * Both gates a bulk "Scrap All" has to satisfy for a row: the cut must consume
 * every piece still pending on the reservation, and the remnant must be
 * scrap-sized by the dimensional rule above.
 *
 * Scrapping a remnant the rule considers inventory remains available as a manual
 * per-row override — a deliberate escape hatch for damaged or wrong material.
 * A bulk action must not do it silently, which is how long remnants ended up
 * Scrapped and parked on the Shop Floor.
 */
export const qualifiesAsScrapForBulk = ({
	reservation,
	enteredCutQty,
	remainingLength,
}: {
	reservation: any;
	enteredCutQty: number;
	remainingLength: number;
}): boolean => {
	if (!reservation) return false;

	const pendingQty =
		Number(reservation.material_cut_quantity ?? 0) -
		Number(reservation.actual_cut_quantity ?? 0);
	if (!(enteredCutQty >= pendingQty)) return false;

	return calculateScrapStatus(reservation, remainingLength);
};
